import { PageContextProps } from 'application/contexts/PageContext';
import Application from 'application/entities/Application';
import { parseUmbracoMacro } from 'helpers/parseUmbracoMacro';
import jsonpath from 'jsonpath';
import { queryCmsContent } from './_api';
import { getFiltersContent } from './filtersContentConnector';
import { getPageSectionContent } from './pageSectionConnector';
import { queryCDA } from './queryCDA';

export const getPageContentFromCms = async (requestUrl: string, pageContext: Partial<PageContextProps>): Promise<Content.PageContent> => {
	const query = `{
		page: page {
			id
			name
			documentType
			url
			properties
			childPages: children {
				id
				documentType
				name
				url
				properties(fields:["umbracoNaviHide", "navigationTitle", "navigationDescription", "highlighted"])
			}
			template
			culture
			parentId
			pageLanguageVersions { 
				cultureName 
				pageUrl
			}
			lastUpdated
			createDate
		}
		breadcrumb
		footerHeading: field(path:"${requestUrl}", field: "footerHeading") 
		footerContent: field(path:"${requestUrl}", field: "footerContent") 
		segment: field(path:"${requestUrl}", field: "segment") 
		hideDates: field(path:"${requestUrl}", field: "hideDates") 
	}`;

	const data = await queryCmsContent({
		query,
		pageContext,
		url: requestUrl,
		requestType: 'pageContent',
	});

	const pageContent: Content.PageContent = {
		...data,
		footer: {
			heading: data?.footerHeading?.footerHeading || null,
			content: data?.footerContent?.footerContent || null,
		},
		segment:
			data?.segment?.segment?.length > 0
				? data.segment.segment.map((segment) => {
						return {
							name: segment?.properties?.segmentName || null,
							trackingName: segment?.properties?.trackingName || null,
							id: segment?.id || null,
						};
				  })
				: null,
		configuration: {
			hideDates: data?.hideDates?.hideDates || null,
		},
	};
	return pageContent;
};

export const getPageContentFromCDA = async (
	requestUrl: string,
	pageContext: Partial<PageContextProps>,
	siteId: string,
): Promise<Content.PageContent> => {
	const pageData = await queryCDA({
		pageContext,
		url: 'umbraco/delivery/api/v2/content/item' + (requestUrl === '/' ? '' : requestUrl),
		urlParams: {
			expand: 'properties[$all[properties[$all[properties[$all[properties[$all[properties[$all[properties[$all[properties[$all[properties[$all[properties[$all]]]]]]]]]]]]]]]]]',
		},
		startItem: siteId,
		requestType: 'pageContent',
	});

	const { contentType, name, cultures, createDate, id, route, properties } = pageData ?? {};

	const { items: ancestors } = await queryCDA({
		pageContext,
		url: 'umbraco/delivery/api/v2/content',
		urlParams: {
			fetch: `ancestors:${requestUrl}`,
			fields: 'properties[footerContent,footerHeading,hideDates,highlighted,navigationDescription,navigationTitle,umbracoNaviHide]',
			expand: 'properties[$all[properties[$all[properties[$all]]]]]',
			sort: 'level:desc',
		},
		startItem: siteId,
		requestType: 'pageContent',
	});

	const moduleTypes = jsonpath.query(pageData.properties, '$..items[?(@.content.contentType=="transportLinks")]');
	const allowedContentTypes = ['documentCatalogPage', 'transportPage'];
	let children = [];
	if (allowedContentTypes.includes(contentType) || moduleTypes.length > 0) {
		const childrenData = await queryCDA({
			pageContext,
			url: 'umbraco/delivery/api/v2/content',
			urlParams: {
				fetch: `children:${requestUrl}`,
				fields: 'properties[hideSiblingMenu,highlighted,navigationDescription,navigationTitle,segment,umbracoNaviHide]',
				sort: 'sortOrder:asc',
				take: '100',
			},
			startItem: siteId,
			requestType: 'pageContent',
		});

		children = childrenData.items;
	}

	const getBreadcrumbs = () => {
		const currentItem = {
			id,
			title: props.navigationTitle ?? name,
			url: route?.path,
		};

		if (ancestors.length === 0) {
			const breadcrumbs = [];
			breadcrumbs.push(currentItem);
			return breadcrumbs;
		}

		const breadcrumbs = ancestors?.map((ancestor) => {
			return {
				id: ancestor.id,
				title: ancestor.properties.navigationTitle ?? ancestor.name,
				url: ancestor.route.path,
			};
		});

		breadcrumbs.push(currentItem);
		breadcrumbs.sort((a, b) => a.url.length - b.url.length);

		return breadcrumbs;
	};

	const getInheritedValue = (key: string) => {
		return properties?.[key] ?? ancestors.find((ancestor) => ancestor?.properties?.[key])?.properties?.[key];
	};

	const transformMarkup = (data) => {
		if (Array.isArray(data)) {
			return data.map((item) => transformMarkup(item));
		} else if (typeof data === 'object' && data !== null) {
			const result = {};
			Object.entries(data).forEach(([key, value]) => {
				if (key === 'markup') {
					result[key] = parseUmbracoMacro(value);
				} else {
					result[key] = transformMarkup(value);
				}
			});
			return result;
		}
		return data; // Return non-object, non-array values as is
	};

	const segment = getInheritedValue('segment');

	const props = {
		...properties,
		filters: await getFiltersContent(properties.filters, pageContext),
		pageSection: await getPageSectionContent(properties.pageSection, pageContext),
		pageSections: await getPageSectionContent(properties.pageSections, pageContext),
	};

	const pageContent: Content.PageContent = {
		page: {
			documentType: contentType,
			template: 'N/A',
			culture: Application?.locale?.toString(),
			createDate,
			pageLanguageVersions: Object.keys(cultures)?.map((cultureName) => ({ cultureName, pageUrl: cultures[cultureName].path })),
			id,
			parent: {
				id: ancestors.length > 0 ? ancestors?.[0].id : null,
				name:
					ancestors.length > 0
						? ancestors?.[0].properties?.navigationTitle
							? ancestors?.[0].properties?.navigationTitle
							: ancestors?.[0].name
						: '',
			},
			name,
			url: route?.path,
			properties: transformMarkup(props),
			childPages:
				children === undefined
					? null
					: children?.map((child) => {
							const { id, contentType, name, route, properties } = child ?? {};
							return {
								id,
								documentType: contentType,
								name: name,
								url: route.path,
								properties: {
									highlighted: properties?.highlighted,
									navigationTitle: properties?.navigationTitle,
									navigationDescription: properties?.navigationDescription,
									umbracoNaviHide: properties?.umbracoNaviHide,
								},
							};
					  }),
			lastUpdated: pageData.updateDate,
		},
		footer: {
			heading: getInheritedValue('footerHeading') || null,
			content: getInheritedValue('footerContent')?.items ?? null,
		},
		breadcrumb: getBreadcrumbs(),
		segment:
			segment?.length > 0
				? segment.map((segment) => {
						return {
							name: segment?.properties?.segmentName || null,
							trackingName: segment?.properties?.trackingName || null,
							id: segment?.id || null,
						};
				  })
				: null,
		configuration: {
			hideDates: getInheritedValue('hideDates') || null,
		},
	};
	return pageContent;
};
